// ============================================================
// 天水數位店長 — App Entry
// ============================================================

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { api } from './lib/api';
import { LoginPage } from './components/LoginPage';
import { PinLoginPage } from './components/PinLoginPage';
import { KioskPage } from './components/KioskPage';
import { WeeklyScheduler } from './components/WeeklyScheduler';
import { ClockInPage } from './components/ClockInPage';
import { ApprovalDashboard } from './components/ApprovalDashboard';
import { LeavePage } from './components/LeavePage';
import { StaffListPage } from './components/StaffListPage';
import { SettingsPage } from './components/SettingsPage';
import { DashboardPage } from './components/DashboardPage';
import { AttendanceReportPage } from './components/AttendanceReportPage';
import { RevenueReportPage } from './components/RevenueReportPage';
import { LeaveOverviewPage } from './components/LeaveOverviewPage';
import { PayrollPage } from './components/PayrollPage';
import { HolidayPage } from './components/HolidayPage';
import { MySchedulePage } from './components/MySchedulePage';
import { PasswordPage } from './components/PasswordPage';
import { MyHomePage } from './components/MyHomePage';
import { AnnouncementPage } from './components/AnnouncementPage';
import { MyPayrollPage } from './components/MyPayrollPage';
import { MyAttendancePage } from './components/MyAttendancePage';
import { CompLeavePage } from './components/CompLeavePage';
import { AttendanceCorrectionPage } from './components/AttendanceCorrectionPage';
import { KioskQrPage } from './components/KioskQrPage';
import { ShiftTemplatePage } from './components/ShiftTemplatePage';
import { Navigation } from './components/Navigation';
import { InstallPrompt } from './components/InstallPrompt';

// 受保護的路由佈局：需登入 + 底部導覽列
function ProtectedLayout() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [pendingCount, setPendingCount] = React.useState(0);

  const isManager = user?.role === 'OWNER' || user?.role === 'MANAGER';

  // 簽核徽章的數字。
  // Navigation 一直有 pendingCount 這個 prop，但從來沒人傳過，
  // 所以紅點永遠是 0 —— 老闆看不出有東西待處理。
  // 換頁時重新取一次，處理完簽核回到別頁數字就會更新。
  useEffect(() => {
    if (!isManager) { setPendingCount(0); return; }
    let cancelled = false;
    api.get<{ total: number }>('/attendance/pending-count')
      .then((d) => { if (!cancelled) setPendingCount(d.total); })
      .catch(() => { /* 非關鍵，徽章不顯示即可 */ });
    return () => { cancelled = true; };
  }, [isManager, location.pathname]);

  if (!user) {
    return <Navigate to="/pin-login" state={{ from: location }} replace />;
  }

  return (
    <div className="min-h-screen bg-morandi-50 font-sans text-morandi-700 pb-nav md:pb-0 md:pl-56 lg:pl-64">
      <Outlet />
      <Navigation pendingCount={pendingCount} />
      <InstallPrompt />
    </div>
  );
}

/**
 * 主管專用路由。
 *
 * 【為什麼需要】原本完全沒有角色守衛，員工只要在網址列輸入 /staff、
 * /payroll、/revenue-report 就能開啟主管頁面。後端權限已補齊，
 * 但沒有這層守衛的話員工會看到空白或壞掉的畫面而以為系統故障。
 * 導覽列的 roles 過濾只是隱藏選項，不構成保護。
 */
/** 依角色決定首頁：員工進個人首頁，主管進總覽 */
function HomeRedirect() {
  const { user } = useAuthStore();
  const isManager = user?.role === 'OWNER' || user?.role === 'MANAGER';
  return <Navigate to={isManager ? '/dashboard' : '/my'} replace />;
}

function ManagerOnlyLayout() {
  const { user } = useAuthStore();
  if (user && user.role !== 'OWNER' && user.role !== 'MANAGER') {
    return <Navigate to="/my" replace />;
  }
  return <Outlet />;
}

export default function App() {
  const { restoreSession } = useAuthStore();

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/pin-login" element={<PinLoginPage />} />
        {/* 店內打卡站：不需登入，以獨立的打卡站憑證運作（見 KioskPage） */}
        <Route path="/kiosk" element={<KioskPage />} />

        {/* Protected */}
        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/clock" element={<ClockInPage />} />
          <Route path="/leaves" element={<LeavePage />} />

          {/* 主管專用 */}
          <Route element={<ManagerOnlyLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/schedule" element={<WeeklyScheduler />} />
            <Route path="/approvals" element={<ApprovalDashboard />} />
            <Route path="/staff" element={<StaffListPage />} />
            <Route path="/attendance-report" element={<AttendanceReportPage />} />
            <Route path="/revenue-report" element={<RevenueReportPage />} />
            <Route path="/leave-overview" element={<LeaveOverviewPage />} />
            <Route path="/payroll" element={<PayrollPage />} />
            <Route path="/holidays" element={<HolidayPage />} />
            <Route path="/shift-templates" element={<ShiftTemplatePage />} />
            <Route path="/announcements" element={<AnnouncementPage />} />
            <Route path="/kiosk-qr" element={<KioskQrPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
          <Route path="/my" element={<MyHomePage />} />
          <Route path="/my-payroll" element={<MyPayrollPage />} />
          <Route path="/my-attendance" element={<MyAttendancePage />} />
          <Route path="/comp-leave" element={<CompLeavePage />} />
          <Route path="/attendance-correction" element={<AttendanceCorrectionPage />} />
          <Route path="/my-schedule" element={<MySchedulePage />} />
          <Route path="/password" element={<PasswordPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
